'use client';

import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Breadcrumbs, IBreadcrumbItem } from './breadcrumbs';
import { EasyTab, EasyTabItem, EasyTabContent } from './easy-tab/easy-tab-component';
import { SearchInput } from './search-input';
import { LucideIcon } from 'lucide-react';
import { ResourceHeaderOutlet } from './resource-header-portal';
import { Icon } from '@ui/components/atoms/icon/icon';

export interface ITabItem {
  label: string;
  labelTextAlias?: string;
  disabled?: boolean;
  icon?: LucideIcon | JSX.Element;
  content?: React.ReactNode;
  className?: string;
  whenActiveClassName?: string;
  whenActiveTextClassName?: string;
  useBottomActiveBorder?: boolean;
  useFullBorderWhenActive?: boolean;
  title?: string;
  unreadCount?: number;
}

export interface IResourceNavigation {
  tabs?: ITabItem[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  selectedTabIndex?: number;

  breadcrumbs?: IBreadcrumbItem[];
  breadcrumbSeparator?: React.ReactNode;
  breadcrumbClassName?: string;

  className?: string;
  showBreadcrumbs?: boolean;

  children?: React.ReactNode;

  tabContainerClassName?: string;
  tabItemClassName?: string;

  title?: string;
  showResourceNavigationTitle?: boolean;

  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearchClick?: () => void;
  searchClassName?: string;

  actionButtons?: React.ReactNode;
  actionButtonsClassName?: string;

  additionalContent?: React.ReactNode;
  showAdditionalContent?: boolean;

  /** Keep the navigation chrome mounted without owning the page body. */
  renderTabContent?: boolean;
}

export function ResourceNavigation({
  tabs = [],
  activeTab,
  onTabChange,
  selectedTabIndex,
  breadcrumbs = [],
  breadcrumbSeparator = '/',
  breadcrumbClassName = '',
  className = '',
  showBreadcrumbs = true,
  children,
  tabContainerClassName = 'flex gap-2  border border-gray-300 rounded-full w-fit p-1 bg-gray-50',
  tabItemClassName = 'min-w-20 px-4 py-2',
  showResourceNavigationTitle = true,
  showSearch = true,
  searchPlaceholder = 'Search...',
  onSearchClick,
  searchClassName = '',
  actionButtons,
  actionButtonsClassName = '',
  additionalContent,
  showAdditionalContent = false,
  renderTabContent = true,
}: IResourceNavigation) {
  const currentActiveTab = activeTab || tabs[0]?.label;
  const activeTabData = tabs.find((tab) => tab.label === currentActiveTab);
  const currentTitle =
    selectedTabIndex !== undefined && tabs[selectedTabIndex]
      ? tabs[selectedTabIndex].title
      : activeTabData?.title;

  const hasTabs = tabs.length > 0;
  const hasBreadcrumbs = breadcrumbs.length > 0;

  if (!hasTabs && !hasBreadcrumbs) {
    return null;
  }

  return (
    <div className={twMerge('flex flex-col gap-4', className)}>
      {hasTabs && (
        <EasyTab
          key={activeTab}
          defaultTab={activeTab || tabs[0]?.label}
          onTabChanged={onTabChange}
          className={twMerge(
            renderTabContent && 'min-h-[200px]',
            'overflow-hidden flex flex-col gap-2',
          )}>
          <ResourceHeaderOutlet>
            <div className="flex flex-col gap-2 min-w-0">
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center min-w-0">
                <div className={twMerge(tabContainerClassName, 'flex-shrink-0')}>
                  <div className="flex justify-between gap-2 w-fit">
                    <div className="flex gap-2">
                      {tabs.map((tab) => (
                        <EasyTabItem
                          key={tab.label}
                          label={tab.label}
                          labelTextAlias={tab.labelTextAlias}
                          disabled={tab.disabled}
                          icon={tab.icon}
                          useFullBorderWhenActive={tab.useFullBorderWhenActive ?? false}
                          useBottomActiveBorder={tab.useBottomActiveBorder ?? false}
                          className={twMerge(tabItemClassName, tab.className)}
                          whenActiveClassName={
                            tab.whenActiveClassName ?? 'bg-primary !text-white rounded-full'
                          }
                          whenActiveTextClassName={tab.whenActiveTextClassName ?? 'text-white'}
                          textClassName="text-xs"
                          badge={
                            tab.unreadCount ? (
                              <div className="z-20 bg-white rounded-full flex items-center justify-center w-5 h-5 shadow-sm border border-gray-100">
                                <Icon
                                  type="solidCommunity"
                                  className="w-4 h-4"
                                  fill="#FF600D"
                                  stroke="#FF600D"
                                />
                              </div>
                            ) : null
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {showBreadcrumbs && hasBreadcrumbs && (
                  <div className="flex items-center min-w-0 max-w-full overflow-hidden flex-1 sm:min-w-[0]">
                    <Breadcrumbs
                      items={breadcrumbs}
                      separator={breadcrumbSeparator}
                      className={breadcrumbClassName}
                    />
                  </div>
                )}
              </div>

              {(currentTitle || showSearch || actionButtons) && (
                <div className="flex justify-between items-center gap-4">
                  {currentTitle && showResourceNavigationTitle && (
                    <div className="flex-shrink-0">
                      <h2 key={currentTitle} className="font-nunito text-lg font-semibold">
                        {currentTitle}
                      </h2>
                    </div>
                  )}

                  <div className="flex items-center gap-3 ml-auto">
                    <SearchInput
                      placeholder={searchPlaceholder}
                      onSearchClick={onSearchClick}
                      className={searchClassName}
                      showSearch={showSearch}
                    />

                    {actionButtons && (
                      <div className={twMerge('flex items-center gap-2', actionButtonsClassName)}>
                        {actionButtons}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Content Section */}
              {showAdditionalContent && additionalContent && <div>{additionalContent}</div>}
            </div>
          </ResourceHeaderOutlet>

          {renderTabContent &&
            tabs.map((tab) => (
              <EasyTabContent key={tab.label} label={tab.label}>
                {tab.content}
              </EasyTabContent>
            ))}
        </EasyTab>
      )}
      {!hasTabs && children}
    </div>
  );
}
